import type { VoidFunctionComponent } from 'react';
import { useContext } from 'react';
import qs from 'qs';
import { RouterContext, useRouter } from '../../../../contexts/RouterContext';
import { formatDateWithTime } from '../../../../helpers/date';
import CardBanner from '../../../generic/CardBanner';
import icons from '../../../generic/Icon/icons';
import { getBaseOrgProperties, track } from '../../../../helpers/analytics';

type SubscriptionBannerTrialInProgressProps = {
  hasPaymentInfo: boolean,
  trialDaysRemaining: number,
  endDate: number,
};
const SubscriptionBannerTrialInProgress: VoidFunctionComponent<SubscriptionBannerTrialInProgressProps> = ({
  hasPaymentInfo,
  trialDaysRemaining,
  endDate,
}) => {
  const {
    getRouter,
    getLocation,
  } = useContext(RouterContext);
  const { pathname } = getLocation();
  const isBillingPage = pathname.startsWith('/account/settings/billing');

  if (isBillingPage) {
    return (<SubscriptionBannerTrialInProgressBillingPage
      hasPaymentInfo={hasPaymentInfo}
      trialDaysRemaining={trialDaysRemaining}
      endDate={endDate}
    />);
  }

  return (
    <CardBanner
      title={`Trial in Progress - ${trialDaysRemaining} days remaining!`}
      description={`Your free trial will end on ${formatDateWithTime(new Date(endDate * 1000))}.`}
      icon={icons.creditCard}
      cta={
        {
          text: 'View more details',
          onClick: () => {
            track(`View More Details Upsell - Banner`, { ...getBaseOrgProperties(), page: pathname });
            getRouter().push(`/account/settings/billing`);
          },
          isFullWidth: true,
        }
      }
    />);
};

const SubscriptionBannerTrialInProgressBillingPage:VoidFunctionComponent<SubscriptionBannerTrialInProgressProps> = ({
  hasPaymentInfo,
  trialDaysRemaining,
  endDate,
}) => {
  const router = useRouter();
  const trialEnd = `Your free trial will end on ${formatDateWithTime(new Date(endDate * 1000))}.`;
  const trialEndNote = hasPaymentInfo
    ? `At the end of your trial, you will be automatically billed based on your subscription.`
    : `At the end of your trial, you will be automatically downgraded to the Free plan.`;

  const title = hasPaymentInfo
    ? `Trial in Progress - ${trialDaysRemaining} days remaining!`
    : `Your trial will expire soon. Please, update your payment method!`;

  return (
    <CardBanner
      title={title}
      description={<><b>{trialEnd}</b> {trialEndNote}</>}
      icon={icons.creditCard}
      cta={
        {
          text: hasPaymentInfo ? 'Edit Payment Method' : 'Add Payment Method',
          onClick: () => {
            router.push(`/account/settings/billing?${qs.stringify({ tab: 'Payment Method' })}`);
          },
        }
      }
    />);
};

export default SubscriptionBannerTrialInProgress;
